import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, GeoJSON, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { X, Loader2, Wand2, AlertTriangle } from "lucide-react";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";
import ModalPortal from "@/components/ui/modal-portal";
import { DRAW_LAYER, MAX_ZOOM } from "@/lib/mapLayers";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ["#E4572E", "#2E86DE", "#F2C14E", "#9B5DE5", "#00B894", "#FF8C42", "#F15BB5", "#4ECDC4"];
const fmt = (v) => Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function boundsFromGeometry(geometry) {
  const rings = geometry?.type === "Polygon" ? [geometry.coordinates?.[0]]
    : geometry?.type === "MultiPolygon" ? geometry.coordinates.map((p) => p[0]) : [];
  const coords = rings.flat().filter(Boolean);
  if (!coords.length) return null;
  const lats = coords.map((c) => c[1]);
  const lngs = coords.map((c) => c[0]);
  return [[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]];
}

/**
 * Détection automatique des îlots : l'image satellite de la parcelle est découpée en
 * zones homogènes ; l'utilisateur choisit celles à créer, les nomme et leur donne une culture.
 */
export default function IlotDetectModal({ parcel, existingNames = [], cultureOptions = [], defaultCulture = "", onCreated, onClose }) {
  const [state, setState] = useState({ loading: true, zones: [], reason: "" });
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const bounds = useMemo(() => boundsFromGeometry(parcel?.geometry), [parcel]);

  useEffect(() => {
    let cancelled = false;
    authFetch(`${API_BASE}/parcels/${parcel.parcel_id}/ilots/detect`, { method: "POST" })
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Détection impossible");
        return d;
      })
      .then((d) => {
        if (cancelled) return;
        const zones = d.zones || [];
        const used = new Set(existingNames.map((n) => n.toLowerCase()));
        let n = 1;
        const nextName = () => {
          while (used.has(`îlot ${n}`)) n += 1;
          used.add(`îlot ${n}`);
          return `Îlot ${n}`;
        };
        setRows(zones.map((z) => ({ keep: !(z.overlaps || []).length, name: nextName(), culture: defaultCulture })));
        setState({ loading: false, zones, reason: d.reason || "" });
      })
      .catch((e) => { if (!cancelled) setState({ loading: false, zones: [], reason: e.message }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parcel.parcel_id]);

  const setRow = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const chosen = rows.map((r, i) => ({ ...r, zone: state.zones[i], i })).filter((r) => r.keep);

  const create = async () => {
    if (!chosen.length) { toast.error("Cochez au moins une zone"); return; }
    if (chosen.some((r) => !r.name.trim())) { toast.error("Donnez un nom à chaque îlot"); return; }
    setCreating(true);
    let ok = 0;
    for (const r of chosen) {
      const ring = r.zone.geometry.coordinates[0].slice(0, -1);
      try {
        const res = await authFetch(`${API_BASE}/parcels/${parcel.parcel_id}/ilots`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: r.name.trim(), culture: r.culture || "", points: ring.map(([lon, lat]) => ({ lat, lon })) }),
        });
        if (res.ok) ok += 1;
        else {
          const d = await res.json().catch(() => ({}));
          toast.error(`${r.name} : ${typeof d.detail === "string" ? d.detail : "création impossible"}`);
        }
      } catch { toast.error(`${r.name} : erreur de connexion`); }
    }
    setCreating(false);
    if (ok) {
      toast.success(`${ok} îlot${ok > 1 ? "s" : ""} créé${ok > 1 ? "s" : ""}`);
      onCreated();
      onClose();
    }
  };

  return (
    <ModalPortal>
    <div className="fixed inset-0 z-[2200] bg-black/60 flex items-center justify-center p-3" data-testid="ilot-detect-modal">
      <div className="bg-white rounded-2xl overflow-hidden w-full max-w-4xl h-[88dvh] flex flex-col shadow-2xl">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-black/10">
          <Wand2 className="w-4 h-4 shrink-0" style={{ color: "#2f6a92" }} />
          <p className="text-sm font-bold shrink-0">Détecter les îlots</p>
          <p className="text-[11px] text-gray-500 flex-1 min-w-0">
            Zones homogènes repérées sur l'image satellite. Cochez celles à créer : vous pourrez ajuster leurs contours ensuite.
          </p>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/5 shrink-0" aria-label="Fermer"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 min-h-0 flex flex-col md:flex-row">
          <div className="relative flex-1 min-h-[240px]">
            {bounds && (
              <MapContainer bounds={bounds} maxZoom={MAX_ZOOM} className="h-full w-full">
                <TileLayer url={DRAW_LAYER.url} attribution={DRAW_LAYER.attribution} maxZoom={MAX_ZOOM} maxNativeZoom={DRAW_LAYER.maxNativeZoom} />
                <GeoJSON data={{ type: "Feature", geometry: parcel.geometry }} style={{ color: "#2D6A4F", weight: 2.5, fillOpacity: 0.03 }} />
                {state.zones.map((z, i) => (
                  <GeoJSON key={`${i}-${rows[i]?.keep}`} data={{ type: "Feature", geometry: z.geometry }}
                    style={{ color: COLORS[i % COLORS.length], weight: rows[i]?.keep ? 2.5 : 1, dashArray: rows[i]?.keep ? null : "4 3", fillOpacity: rows[i]?.keep ? 0.35 : 0.08 }}
                    eventHandlers={{ click: () => setRow(i, { keep: !rows[i]?.keep }) }}>
                    <Tooltip sticky>{rows[i]?.name} · {fmt(z.surface_ha)} ha</Tooltip>
                  </GeoJSON>
                ))}
              </MapContainer>
            )}
            {state.loading && (
              <div className="absolute inset-0 z-[1000] bg-white/70 flex flex-col items-center justify-center gap-2 text-sm text-gray-600">
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: "#2f6a92" }} />
                Analyse de l'image satellite…
              </div>
            )}
          </div>

          <div className="md:w-80 border-t md:border-t-0 md:border-l border-black/10 flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {!state.loading && !state.zones.length && (
                <p className="text-[12px] text-gray-600 rounded-lg bg-gray-50 p-3">{state.reason || "Aucune zone distincte détectée."} Vous pouvez tracer les îlots à la main.</p>
              )}
              {state.zones.map((z, i) => (
                <div key={i} className={`rounded-lg border p-2 space-y-1.5 ${rows[i]?.keep ? "border-black/20" : "border-black/10 opacity-60"}`}>
                  <label className="flex items-center gap-2 text-[12px] font-semibold cursor-pointer">
                    <input type="checkbox" checked={!!rows[i]?.keep} onChange={(e) => setRow(i, { keep: e.target.checked })} />
                    <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                    Zone {i + 1} · {fmt(z.surface_ha)} ha
                  </label>
                  <p className="text-[11px] text-gray-500">{z.type}</p>
                  {(z.overlaps || []).length > 0 && (
                    <p className="text-[11px] flex items-center gap-1" style={{ color: "#B4532F" }}>
                      <AlertTriangle className="w-3 h-3" /> Recouvre l'îlot {z.overlaps.join(", ")}
                    </p>
                  )}
                  {rows[i]?.keep && (
                    <div className="flex gap-1.5">
                      <input value={rows[i].name} onChange={(e) => setRow(i, { name: e.target.value })}
                        className="h-7 text-xs border border-black/15 rounded-md px-2 w-24" aria-label="Nom de l'îlot" />
                      <select value={rows[i].culture} onChange={(e) => setRow(i, { culture: e.target.value })}
                        className="h-7 text-xs border border-black/15 rounded-md px-1 flex-1 min-w-0 bg-white" aria-label="Culture">
                        <option value="">Culture…</option>
                        {cultureOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="p-3 border-t border-black/10 flex items-center gap-2">
              <span className="text-[11px] text-gray-500 flex-1">{chosen.length} zone{chosen.length > 1 ? "s" : ""} cochée{chosen.length > 1 ? "s" : ""}</span>
              <button onClick={create} disabled={creating || !chosen.length}
                className="h-8 px-3 text-xs font-semibold rounded-md text-white flex items-center gap-1 disabled:opacity-40"
                style={{ background: "#2D6A4F" }} data-testid="ilot-detect-create">
                {creating && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Créer les îlots
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
}
